import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { NotificationService } from '../../../../core/services/notification.service';
import { UserProfileService } from '../../../../core/services/user-profile.service';
import { LayoutActions } from './layout.actions';

export class LayoutEffects {
  private readonly actions$ = inject(Actions);
  private readonly notificationService = inject(NotificationService);
  private readonly userProfileService = inject(UserProfileService);

  readonly loadNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutActions.loadNotifications),
      switchMap(() =>
        forkJoin({
          unreadCount: this.notificationService.getUnreadCount(),
          page: this.notificationService.getNotifications({ page: 1, pageSize: 6 }),
        }).pipe(
          map(({ unreadCount, page }) =>
            LayoutActions.loadNotificationsSuccess({
              notifications: page.items,
              unreadCount,
            }),
          ),
          catchError(() =>
            of(
              LayoutActions.loadNotificationsFailure({
                error: 'layout.header.notificationsPanel.error',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly markAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutActions.markNotificationAsRead),
      switchMap(({ notificationId }) =>
        this.notificationService.markAsRead(notificationId).pipe(
          map(() => LayoutActions.loadNotifications({ showLoading: false })),
          catchError(() =>
            of(
              LayoutActions.loadNotificationsFailure({
                error: 'layout.header.notificationsPanel.error',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly markAllAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutActions.markAllNotificationsAsRead),
      switchMap(() =>
        this.notificationService.markAllAsRead().pipe(
          map(() => LayoutActions.loadNotifications({ showLoading: false })),
          catchError(() =>
            of(
              LayoutActions.loadNotificationsFailure({
                error: 'layout.header.notificationsPanel.error',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly loadProfileImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutActions.loadProfileImage),
      switchMap(() =>
        this.userProfileService.getProfileImageUrl().pipe(
          map((profileImageUrl) => LayoutActions.loadProfileImageSuccess({ profileImageUrl })),
          catchError(() => of(LayoutActions.loadProfileImageFailure())),
        ),
      ),
    ),
  );
}
